import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { BrainCircuit, Send, SkipForward, Pencil, AlertTriangle, CheckCircle, RefreshCw, X, Search, ChevronDown, BarChart3, Clock, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const URGENCY_COLORS = {
  1: "#94A3B8", 2: "#94A3B8", 3: "#64748B",
  4: "#EAB308", 5: "#EAB308", 6: "#F97316",
  7: "#F97316", 8: "#EF4444", 9: "#EF4444", 10: "#DC2626",
};

const STAGE_BADGES = {
  cold: { bg: "#E2E8F0", color: "#475569", label: "Cold" },
  warming: { bg: "#FEF3C7", color: "#92400E", label: "Warming" },
  hot: { bg: "#FED7AA", color: "#9A3412", label: "Hot" },
  ready_to_act: { bg: "#FEE2E2", color: "#991B1B", label: "Ready" },
};

function UrgencyBadge({ score }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold text-white"
      style={{ backgroundColor: URGENCY_COLORS[score] || "#94A3B8" }}
    >
      <Zap size={10} /> {score}/10
    </span>
  );
}

function StageBadge({ stage }) {
  const s = STAGE_BADGES[stage] || STAGE_BADGES.cold;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="card p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div>
        <p className="text-2xl font-bold" style={{ color: "#0F1419" }}>{value}</p>
        <p className="text-xs" style={{ color: "#8899AA" }}>{label}</p>
      </div>
    </div>
  );
}

export default function AiDrafts() {
  const [drafts, setDrafts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [refreshing, setRefreshing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [sending, setSending] = useState({});
  const [classifyId, setClassifyId] = useState("");
  const [classifying, setClassifying] = useState(false);
  const [classifyResult, setClassifyResult] = useState(null);
  const [showClassify, setShowClassify] = useState(false);

  const token = () => localStorage.getItem("token");

  const fetchDrafts = useCallback(async (statusFilter) => {
    try {
      const { data } = await axios.get(`${API}/api/ai-drafts?status=${statusFilter || filter}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setDrafts(data.drafts || []);
    } catch (e) {
      console.error("Failed to fetch AI drafts", e);
    }
  }, [filter]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/ai-drafts/stats`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setStats(data);
    } catch (e) {
      console.error("Failed to fetch stats", e);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDrafts(), fetchStats()]);
      setLoading(false);
    };
    load();
  }, [fetchDrafts, fetchStats]);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDrafts(), fetchStats()]);
    setRefreshing(false);
  };

  const changeFilter = (f) => {
    setFilter(f);
    fetchDrafts(f);
  };

  const handleApprove = async (draft) => {
    const message = editingId === draft.id ? editText : draft.drafted_message;
    setSending((p) => ({ ...p, [draft.id]: "approving" }));
    try {
      await axios.post(`${API}/api/ai-drafts/${draft.id}/approve`, { message }, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setEditingId(null);
      await Promise.all([fetchDrafts(), fetchStats()]);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to send");
    }
    setSending((p) => ({ ...p, [draft.id]: null }));
  };

  const handleSkip = async (id) => {
    setSending((p) => ({ ...p, [id]: "skipping" }));
    try {
      await axios.post(`${API}/api/ai-drafts/${id}/skip`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      await Promise.all([fetchDrafts(), fetchStats()]);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to skip");
    }
    setSending((p) => ({ ...p, [id]: null }));
  };

  const handleClassify = async () => {
    if (!classifyId.trim()) return;
    setClassifying(true);
    setClassifyResult(null);
    try {
      const { data } = await axios.post(`${API}/api/ai-drafts/classify`, { fub_person_id: parseInt(classifyId) }, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setClassifyResult(data);
      await Promise.all([fetchDrafts(), fetchStats()]);
    } catch (e) {
      setClassifyResult({ error: e.response?.data?.detail || "Classification failed" });
    }
    setClassifying(false);
  };

  const FILTERS = [
    { key: "pending", label: "Pending", count: stats.pending },
    { key: "flagged", label: "Flagged", count: stats.flagged },
    { key: "sent", label: "Sent", count: stats.sent },
    { key: "skipped", label: "Skipped", count: stats.skipped },
    { key: "all", label: "All" },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <BrainCircuit size={24} /> AI Drafts
          </h1>
          <p className="page-subtitle">AI-generated follow-up messages awaiting your review</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowClassify(!showClassify)} className="btn btn-secondary text-sm">
            <Search size={14} /> Classify Lead
          </button>
          <button onClick={refresh} className="btn btn-secondary text-sm" disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Pending Review" value={stats.pending || 0} icon={Clock} color="#EAB308" />
        <StatCard label="Flagged" value={stats.flagged || 0} icon={AlertTriangle} color="#F97316" />
        <StatCard label="Sent" value={stats.sent || 0} icon={CheckCircle} color="#22C55E" />
        <StatCard label="Edit Rate" value={`${stats.edit_rate || 0}%`} icon={BarChart3} color="#3B82F6" />
      </div>

      {/* Manual Classify */}
      {showClassify && (
        <div className="card p-4 mb-6">
          <h3 className="font-semibold text-sm mb-2" style={{ color: "#0F1419" }}>Manually Classify a Lead</h3>
          <p className="text-xs mb-3" style={{ color: "#8899AA" }}>Enter a FUB Person ID to run AI intent classification and generate a draft.</p>
          <div className="flex gap-2">
            <input
              type="number"
              value={classifyId}
              onChange={(e) => setClassifyId(e.target.value)}
              placeholder="FUB Person ID"
              className="input text-sm flex-1"
              style={{ maxWidth: 200 }}
            />
            <button onClick={handleClassify} className="btn btn-primary text-sm" disabled={classifying || !classifyId.trim()}>
              {classifying ? "Classifying..." : "Classify & Draft"}
            </button>
          </div>
          {classifyResult && (
            <div className="mt-3 p-3 rounded-lg text-sm" style={{ backgroundColor: classifyResult.error ? "#FEF2F2" : "#F0FDF4" }}>
              {classifyResult.error ? (
                <p style={{ color: "#991B1B" }}>{classifyResult.error}</p>
              ) : (
                <div className="space-y-1">
                  <p><strong>{classifyResult.lead_name}</strong> — draft created</p>
                  <p>Intent: {classifyResult.classification?.intent_type} | Urgency: {classifyResult.classification?.urgency_score}/10</p>
                  <p className="italic" style={{ color: "#64748B" }}>"{classifyResult.drafted_message}"</p>
                  {classifyResult.confidence_flag && (
                    <p className="flex items-center gap-1" style={{ color: "#F97316" }}>
                      <AlertTriangle size={12} /> {classifyResult.confidence_flag}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-1 mb-4 flex-wrap">
        {FILTERS.map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => changeFilter(key)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              backgroundColor: filter === key ? "#FF5C35" : "#F4F5F7",
              color: filter === key ? "white" : "#5F6F7C",
            }}
          >
            {label}{count !== undefined ? ` (${count})` : ""}
          </button>
        ))}
      </div>

      {/* Draft List */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
        </div>
      ) : drafts.length === 0 ? (
        <div className="card p-12 text-center">
          <BrainCircuit size={48} className="mx-auto mb-3" style={{ color: "#CDD5DE" }} />
          <p className="font-semibold" style={{ color: "#5F6F7C" }}>No {filter} drafts</p>
          <p className="text-sm mt-1" style={{ color: "#8899AA" }}>
            {filter === "pending" ? "AI drafts will appear here when new leads or events trigger classification." : `No drafts with status "${filter}".`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map((draft) => {
            const cls = draft.classification || {};
            const isEditing = editingId === draft.id;
            const actionInProgress = sending[draft.id];
            const canAct = draft.status === "pending" || draft.status === "flagged";

            return (
              <div key={draft.id} className="card p-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold" style={{ color: "#0F1419" }}>{draft.lead_name || "Unknown Lead"}</h3>
                      <UrgencyBadge score={cls.urgency_score || 1} />
                      <StageBadge stage={cls.stage_assessment || "cold"} />
                      {cls.intent_type && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: "#EEF2FF", color: "#4338CA" }}>
                          {cls.intent_type}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: "#8899AA" }}>
                      <span>{draft.lead_phone || "No phone"}</span>
                      <span>Stage: {draft.lead_stage || "—"}</span>
                      <span>Trigger: {draft.trigger}</span>
                      <span>{new Date(draft.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {draft.status === "sent" && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: "#DCFCE7", color: "#166534" }}>Sent</span>
                  )}
                  {draft.status === "skipped" && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: "#F1F5F9", color: "#64748B" }}>Skipped</span>
                  )}
                </div>

                {/* Confidence Flag */}
                {draft.confidence_flag && (
                  <div className="flex items-start gap-2 p-2 rounded-lg mb-3" style={{ backgroundColor: "#FFF7ED" }}>
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: "#F97316" }} />
                    <p className="text-xs" style={{ color: "#9A3412" }}>{draft.confidence_flag}</p>
                  </div>
                )}

                {/* AI Reasoning */}
                {cls.reasoning && (
                  <p className="text-xs italic mb-3 px-2" style={{ color: "#8899AA" }}>
                    AI reasoning: {cls.reasoning}
                  </p>
                )}

                {/* Message */}
                <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: "#F8FAFC" }}>
                  {isEditing ? (
                    <div>
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="input text-sm w-full"
                        rows={3}
                        maxLength={320}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs" style={{ color: editText.length > 300 ? "#EF4444" : "#8899AA" }}>
                          {editText.length}/320
                        </span>
                        <button onClick={() => setEditingId(null)} className="text-xs" style={{ color: "#8899AA" }}>
                          Cancel edit
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: "#0F1419" }}>"{draft.drafted_message}"</p>
                  )}
                </div>

                {/* Recommended action */}
                {cls.recommended_action && (
                  <p className="text-xs mb-3" style={{ color: "#64748B" }}>
                    Recommended: <strong>{cls.recommended_action.replace(/_/g, " ")}</strong>
                  </p>
                )}

                {/* Actions */}
                {canAct && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApprove(draft)}
                      disabled={!!actionInProgress}
                      className="btn btn-primary text-sm"
                    >
                      <Send size={14} /> {actionInProgress === "approving" ? "Sending..." : (isEditing ? "Send Edited" : "Approve & Send")}
                    </button>
                    {!isEditing && (
                      <button
                        onClick={() => { setEditingId(draft.id); setEditText(draft.drafted_message); }}
                        className="btn btn-secondary text-sm"
                      >
                        <Pencil size={14} /> Edit
                      </button>
                    )}
                    <button
                      onClick={() => handleSkip(draft.id)}
                      disabled={!!actionInProgress}
                      className="btn btn-secondary text-sm"
                    >
                      <SkipForward size={14} /> {actionInProgress === "skipping" ? "Skipping..." : "Skip"}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
